import React, { useState, useRef } from 'react';
import {
  useNavigate,
  useSearchParams,
  useLocation,
  useParams,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import MainSearch from '../@generalComponents/layout/MainSearch/MainSearch.jsx';
import DatePickerButton from '../../stories/general-components/DatePickerButton.jsx';
import RangePickerButton from '../../stories/general-components/RangePickerButton.jsx';
import * as Icons from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
  Menu,
  MenuItem,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Toolbar,
  ButtonBase,
  Box,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const TopNavBar = ({
  isTablet,
  pageTitle,
  actions,
  isMobile,
  iconEmoji,
  searchActivated,
  elementId,
  formatedPath,
  iconMain,
  manualIndex,
  usersInRoom,
  tabs,
  sectionTitle,
  subTitle,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { pathname: currentPath } = useLocation();
  const longPressTimeout = useRef(null);
  const { moduleId, structureId } = useParams();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [isHovered, setIsHovered] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const businessStructure = useSelector(
    (state) => state.core.businessStructure
  );

  const modules = businessStructure?.modules || [];
  const activeModule = modules?.find((m) => m.id === moduleId);
  const localStorageStart = localStorage.getItem(activeModule?.id + 'start');
  const localStorageEnd = localStorage.getItem(activeModule?.id + 'end');

  const [anchorElSelect, setAnchorElSelect] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [longPress, setLongPress] = useState(false);
  const [dateRange, setDateRange] = useState([
    activeModule?.list?.preferences?.customizations?.backToToday
      ? moment().startOf('day')?.format('YYYY-MM-DD')
      : localStorageStart
        ? moment.unix(localStorageStart)?.format('YYYY-MM-DD')
        : moment().startOf('day').format('YYYY-MM-DD'),
    activeModule?.list?.preferences?.customizations?.backToToday
      ? moment().endOf('day')?.format('YYYY-MM-DD')
      : localStorageEnd
        ? moment.unix(localStorageEnd)?.format('YYYY-MM-DD')
        : moment().endOf('day').format('YYYY-MM-DD'),
  ]);
  const [currentSelect, setCurrentSelect] = useState();
  const [openSelect, setOpenSelect] = useState(false);
  const [clicked, setClicked] = useState(false);

  const handleDateRangeChange = (newDateRange) => {
    setSearchParams(searchParams);
    if (actions?.onChangeDateRange?.rangeDates === 'single') {
      setDateRange([newDateRange[0], newDateRange[0]]);
      searchParams.set('startDate', newDateRange[0]?.format('YYYY-MM-DD'));
      searchParams.set('endDate', newDateRange[1]?.format('YYYY-MM-DD'));
      actions?.onChangeDateRange?.action &&
        actions?.onChangeDateRange.action([newDateRange[0], newDateRange[0]]);
    } else if (actions?.onChangeDateRange?.rangeDates !== 'none') {
      setDateRange(newDateRange);
      searchParams.set('startDate', newDateRange[0]?.format('YYYY-MM-DD'));
      searchParams.set('endDate', newDateRange[1]?.format('YYYY-MM-DD'));
      actions?.onChangeDateRange?.action &&
        actions?.onChangeDateRange.action(newDateRange);
    }
  };

  const [activeIndex, setActiveIndex] = useState(0);

  const InnerShadowIconButton = React.forwardRef(
    ({ icon, onClick, disabled, noMargin, transparent, ...props }, ref) => {
      const IconComponent = Icons[icon] || Icons.Error;

      return (
        <span>
          <IconButton
            ref={ref}
            {...props}
            size="large"
            edge="start"
            color="black"
            onClick={typeof onClick === 'function' ? onClick : undefined}
            disabled={disabled}
            sx={{
              borderRadius: '6px',
              ml: noMargin ? 0 : 1,
              backgroundColor: transparent
                ? 'transparent'
                : isDarkMode
                  ? '#00000090'
                  : '#FFFFFF95',
              padding: '0px',
              height: '42px',
              minHeight: '42px',
              width: '42px',
              maxHeight: '42px',
              position: 'relative',
              '& svg': {
                position: 'relative',
                zIndex: 2,
              },
            }}
          >
            <IconComponent color={isDarkMode ? 'white' : 'black'} />
          </IconButton>
        </span>
      );
    }
  );

  const updateTabIndexInURL = (index) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('tab', index);
    navigate(`${pathname}?${searchParams.toString()}`);
  };

  const handleTabChange = (event, id) => {
    setActiveIndex(id);
    updateTabIndexInURL(id);
  };

  const businessPreference = useSelector((state) => state.core.businessData);
  const currentUser = useSelector((state) => state.core.user);

  const activeStructure = businessStructure?.structures?.find(
    (structure) => structure.id === structureId
  );
  const businessModules = businessStructure?.modules;

  const module = businessModules?.find((m) => m.id === moduleId);

  const getModuleName = () => {
    if (pageTitle) {
      return pageTitle;
    }

    return module?.name;
  };

  const history = localStorage.getItem('history')
    ? JSON.parse(localStorage.getItem('history'))
    : [];

  const filtredHistory = history.filter(
    (loc) => loc.businessId === businessPreference?.id
  );

  const navigateBack = () => {
    const history = localStorage.getItem('history')
      ? JSON.parse(localStorage.getItem('history'))
      : [];
    const lastElementMinusOne = history[history?.length - 2];
    navigate(lastElementMinusOne?.url || lastElementMinusOne?.pathname);
  };

  const handleSelect = (item) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('selection', item?.value);
    navigate(`${pathname}?${searchParams.toString()}`);
    setCurrentSelect(item);
    setAnchorElSelect(null);
    setOpenSelect(false);
  };

  const handleCloseSelect = () => {
    setAnchorElSelect(null);
    setOpenSelect(false);
  };

  const handleOpenSelect = () => {
    setAnchorElSelect(anchorElSelect);
    setOpenSelect(true);
  };


  const handleButtonPress = (e) => {
    setLongPress(false);
    longPressTimeout.current = setTimeout(() => {
      setLongPress(true);
      setMenuAnchor(e.target);
    }, 750);
  };

  const handleButtonRelease = () => {
    clearTimeout(longPressTimeout.current);
    if (!longPress) {
      navigateBack();
    }
  };


  const IconComponent = module?.icon
    ? Icons[module?.icon]
    : activeStructure?.icon
      ? Icons[activeStructure?.icon]
      : Icons[iconMain]
        ? Icons[iconMain]
        : null

  return (
    <div
      style={{
        position: 'relative',
        height: '55px',
        maxHeight: '55px',
        paddingBottom: '20px',
        minHeight: '55px',
      }}
    >

      <Toolbar
        sx={{
          height: '45px',
          minHeight: '45px',
          maxHeight: '45px',
          borderRadius: '6px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignContent: 'left',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
          flex={isMobile ? 0.5 : searchActivated ? 7 : 4}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'flex-start',
              width: isMobile ? '30px' : '100%',
              marginRight: '40px',
              maxWidth: isMobile ? '30px' : searchActivated ? '200px' : '23vh',
              minWidth: isMobile ? '30px' : searchActivated ? '200px' : '23vh',
            }}
            flex={isMobile ? 0.2 : searchActivated ? 4 : 2}
          >
            <div style={{ marginTop: '5px' }}>
              <span
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className="d-flex middle-content"
              >
                {' '}
                <div>
                  <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    elevation={3}
                    sx={{
                      marginTop: '18px',
                      marginLeft: '-14px',
                      '& .MuiPaper-root': {
                        borderRadius: '6px',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 200,
                          overflowY: 'auto',
                          borderRadius: '6px',
                        },
                      },
                    }}
                  >
                    {filtredHistory?.map((loc, index) => (
                      <MenuItem
                        divider
                        dense
                        key={index}
                        onClick={() => navigate(loc?.pathname)}
                      >
                        <div>
                          <Typography
                            variant="body1"
                            fontWeight={600}
                            fontSize="12px"
                            style={{
                              color: isDarkMode ? '#FFFFFF' : '#000000',
                            }}
                          >
                            {loc?.name}
                          </Typography>

                          <Typography
                            variant="body1"
                            fontWeight={400}
                            fontSize="11px"
                            style={{
                              color: isDarkMode ? '#ffffff60' : '#00000060',
                            }}
                          >
                            {loc?.moduleName}
                          </Typography>
                        </div>
                      </MenuItem>
                    ))}
                  </Menu>
                </div>
                {(isHovered || isMobile) ? (
                  <Tooltip title={t('navigateBack')}>
                    <IconButton
                      sx={{
                        width: '25px',
                        height: '25px',
                        maxHeight: '25px',
                        maxWidth: '25px',
                        minWidth: '25px',
                        borderRadius: '6px',
                        padding: '0px',
                      }}
                      size="medium"
                      onMouseDown={handleButtonPress}
                      onMouseUp={handleButtonRelease}
                      onTouchStart={handleButtonPress}
                      onTouchEnd={handleButtonRelease}
                    >
                      <Icons.ArrowBack
                        sx={{
                          fontSize: '20px',
                        }}
                        htmlColor={isDarkMode ? '#ffffff' : '#000000'}
                      />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <>
                    <IconButton
                      sx={{
                        width: '25px',
                        height: '25px',
                        maxHeight: '25px',
                        maxWidth: '25px',
                        minWidth: '25px',
                        borderRadius: '6px',
                        padding: '0px',
                      }}
                      size="medium"
                    >
                      {iconMain?.startsWith('https') ? <img src={iconMain} alt="icon" style={{ width: '25px', height: '25px' }} /> : (iconMain || module?.icon || activeStructure?.icon) ? (
                        <IconComponent
                          sx={{
                            fontSize: '16px',
                          }}
                          htmlColor={isDarkMode ? '#ffffff' : '#000000'}
                        />) : (<div>{iconEmoji}</div>)}
                    </IconButton>
                  </>
                )}
                {!isMobile && (
                  <Tooltip title={getModuleName(pageTitle, currentLanguage)}>
                    <Typography
                      sx={{
                        maxWidth: '23vh',
                        overflow: 'hidden',
                        marginLeft: '6px',
                        textOverflow: 'ellipsis',
                        color: isDarkMode ? '#FFF' : '#000',
                        whiteSpace: 'nowrap',
                      }}
                      fontSize={isTablet ? '16px' : '18px'}
                      fontWeight={600}
                    >
                      {actions?.editLayout?.display
                        ? t('editMode')
                        : getModuleName(pageTitle, currentLanguage)}
                    </Typography>
                  </Tooltip>)}
              </span>
              {!isMobile && (
                <div className="d-flex" style={{ marginTop: '-5px' }}>
                  <div>
                    <Typography
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: 'grey',
                        marginTop: '4.5px',
                        whiteSpace: 'nowrap',
                      }}
                      fontSize={isTablet ? '10px' : '11px'}
                      fontWeight={300}
                    >
                      {(activeStructure?.name || subTitle || pageTitle || '') +
                        (sectionTitle ? ' - ' + (sectionTitle || '') : '')}
                    </Typography>
                  </div>
                </div>)}
            </div>
          </Box>
          {!currentPath?.startsWith('/bos/element') && searchActivated && (
            <motion.div
              initial={{ width: searchActivated ? '20vh' : '0vh' }}
              animate={{
                width: clicked ? '30vh' : searchActivated ? '20vh' : '0vh',
              }}
              transition={{ duration: 0.5, ease: 'easeInOut' }}
            >
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  minWidth: clicked ? '30vh' : searchActivated ? '20vh' : '0vh',
                }}
                flex={clicked ? 7 : 4}
              >
                <div style={{ marginTop: isMobile ? '10px' : '', marginLeft: isMobile ? '-35px' : '' }}>
                  <MainSearch
                    userLogged={true}
                    userType="business"
                    isDarkMode={isDarkMode}
                    structureCollection={activeStructure?.collectionField}
                    setClicked={setClicked}
                    clicked={clicked}
                  />
                </div>
              </Box>
            </motion.div>
          )}
          <motion.div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'center',
              width: '100%',
            }}
            initial={{ width: '30vh' }}
            animate={{ width: clicked ? '20vh' : '30vh' }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                alignItems: 'center',
                width: '100%',
              }}
              flex={clicked ? 3 : 6}
            >


              {actions?.share && !clicked && !actions?.editLayout?.display && (
                <motion.div whileHover={{ scale: 1.2 }}>
                  <Tooltip title={t('sharableLink')}>
                    <span>
                      <InnerShadowIconButton
                        icon="LinkOutlined"
                        onClick={actions?.share}
                        disabled={actions?.share === 'disabled'}
                      />
                    </span>
                  </Tooltip>
                </motion.div>
              )}


              {!isMobile && actions?.filters &&
                actions?.setFilters &&
                !actions?.editLayout?.display && (
                  <motion.div whileHover={{ scale: 1.2, rotate: 90 }}>
                    <Tooltip title={t('filters')}>
                      <InnerShadowIconButton
                        icon="TuneOutlined"
                        onClick={actions?.setFilters === 'disabled' ? null : actions?.setFilters}
                        disabled={actions?.setFilters === 'disabled'}
                      />
                    </Tooltip>
                  </motion.div>
                )}{' '}
              {actions?.add && !actions?.editLayout?.display && !clicked && (
                <motion.div whileHover={{ scale: 1.2 }}>
                  <Tooltip title={t('create')}>
                    <InnerShadowIconButton
                      icon="Add"
                      onClick={actions?.add === 'disabled' ? null : actions?.add}
                      disabled={actions?.add === 'disabled'}
                    />
                  </Tooltip>
                </motion.div>
              )}
              {actions?.edit && !actions?.editLayout?.display && !clicked && (
                <motion.div whileHover={{ scale: 1.2 }}>
                  <Tooltip title={t('edit')}>
                    <InnerShadowIconButton
                      icon="EditOutlined"
                      onClick={actions?.edit === 'disabled' ? null : actions?.edit}
                      disabled={actions?.edit === 'disabled'}
                    />
                  </Tooltip>
                </motion.div>
              )}
              {actions?.save && !actions?.editLayout?.display && !clicked && (
                <motion.div whileHover={{ scale: 1.2 }}>
                  <Tooltip title={t('save')}>
                    <InnerShadowIconButton
                      icon="SaveOutlined"
                      onClick={actions?.save === 'disabled' ? null : actions?.save}
                      disabled={actions?.save === 'disabled'}
                    />
                  </Tooltip>
                </motion.div>
              )}


              <div
                className="d-flex middle-content"
                style={{
                  backgroundColor: isDarkMode ? '#00000090' : '#FFFFFF95',
                  borderRadius: '6px',
                  marginTop: isMobile ? '8px' : '',
                  marginLeft: isMobile ? '-65px' : '8px',
                }}
              >
                {actions?.previous && !actions?.editLayout?.display && (
                  <InnerShadowIconButton
                    noMargin
                    transparent
                    icon="ArrowBackIos"
                    onClick={actions?.previous}
                    disabled={
                      actions?.previous === 'disabled' ||
                      actions?.currentPage === 1
                    }
                  />
                )}
                {actions?.currentPage &&
                  !actions?.editLayout?.display &&
                  actions?.next && (
                    <Typography
                      variant="subtitle1"
                      component="div"
                      fontWeight={600}
                    >
                      {actions?.currentPage}
                    </Typography>
                  )}
                {actions?.next && !actions?.editLayout?.display && (
                  <InnerShadowIconButton
                    icon="ArrowForwardIos"
                    onClick={actions?.next}
                    transparent
                    disabled={actions?.next === 'disabled'}
                  />
                )}
              </div>
              {!isMobile && actions?.deleteItem && !actions?.editLayout?.display && (
                <motion.div whileHover={{ scale: 1.2 }}>
                  {(currentUser?.activeBusiness?.role === 'SUPER-ADMIN' ||
                    currentUser?.activeBusiness?.role === 'ADMIN') && (
                      <Tooltip title={t('delete')}>
                        <InnerShadowIconButton
                          icon="DeleteOutlineOutlined"
                          onClick={actions?.deleteItem === 'disabled' ? null : actions?.deleteItem}
                          disabled={actions?.deleteItem === 'disabled'}
                        />
                      </Tooltip>
                    )}
                </motion.div>
              )}
              {actions?.select && (
                <div id="select">
                  <ButtonBase
                    sx={{
                      backgroundColor: 'success',
                      color: 'black',
                      fontWeight: 500,
                      paddingLeft: '10px',
                      paddingRight: '10px',
                      paddingTop: '3px',
                      fontSize: '13.5px',
                    }}
                    onClick={handleOpenSelect}
                  >
                    {currentSelect?.label || t('select')}
                  </ButtonBase>

                  <Menu
                    anchorEl={anchorElSelect}
                    open={openSelect}
                    onClose={handleCloseSelect}
                    PaperProps={{
                      style: {
                        maxHeight: 400,
                        width: 200,
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 200,
                          overflowY: 'auto',
                          borderRadius: '6px',
                        },
                      },
                    }}
                  >
                    {actions?.select?.map((item, idx) => (
                      <MenuItem key={idx} onClick={() => handleSelect(item)}>
                        {item?.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </div>
              )}
              {!isMobile && actions?.email && !actions?.editLayout?.display && (
                <motion.div whileHover={{ scale: 1.2 }}>
                  <Tooltip
                    title={t('sendEmail')}
                    key={actions?.email + 'email'}
                  >
                    <InnerShadowIconButton
                      icon="EmailOutlined"
                      onClick={actions?.email === 'disabled' ? null : actions?.email}
                      disabled={actions?.email === 'disabled'}
                    />
                  </Tooltip>
                </motion.div>
              )}
              {!isMobile && actions?.print && !actions?.editLayout?.display && (
                <motion.div whileHover={{ scale: 1.2 }}>
                  <Tooltip title={t('print')} key={actions?.print + 'print'}>
                    <InnerShadowIconButton
                      icon="LocalPrintshopOutlined"
                      onClick={actions?.print === 'disabled' ? null : actions?.print}
                      disabled={actions?.print === 'disabled'}
                    />
                  </Tooltip>
                </motion.div>
              )}
              {actions?.convert?.action && !actions?.editLayout?.display && (
                <motion.div whileHover={{ scale: 1.2 }}>
                  <Tooltip
                    title={t('convert')}
                    key={actions?.convert?.action + 'convert'}
                  >
                    <InnerShadowIconButton
                      icon="SwitchAccessShortcutOutlined"
                      onClick={actions?.convert?.action === 'disabled' ? null : actions?.convert?.action}
                      disabled={actions?.convert?.action === 'disabled'}
                    />
                  </Tooltip>
                </motion.div>
              )}




              {actions?.date && !actions?.editLayout?.display && (
                <div style={{ marginLeft: isMobile ? '-10px' : '8px', marginTop: isMobile ? '10px' : '' }}>
                  <DatePickerButton
                    formatDensity="dense"
                    value={actions?.date}
                    onChange={actions?.onChangeDate}
                  />
                </div>
              )}
              {actions?.onChangeDateRange && !actions?.editLayout?.display && (
                <div style={{ marginLeft: isMobile ? '-10px' : '8px', marginTop: isMobile ? '10px' : '' }}>
                  <RangePickerButton
                    formatDensity="dense"
                    value={dateRange}
                    type={actions?.onChangeDateRange?.rangeDates}
                    onChange={handleDateRangeChange}
                  />
                </div>
              )}
            </Box>
          </motion.div>

          {actions?.approve && !actions?.editLayout?.display && (
            <ButtonBase
              sx={{
                borderRadius: '6px',
                backgroundColor: 'success',
                color: 'black',
                fontWeight: 500,
                fontSize: '12px',
              }}
              onClick={actions?.approve}
            >
              {t('sendApproval')}
            </ButtonBase>
          )}
        </Box>

        <Box
          sx={{
            alignItems: 'right',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
          flex={isMobile ? 1 : 2}
        >
          <Tabs
            sx={{
              height: '42px',
              maxHeight: '42px',
              minHeight: '42px',
              backgroundColor: isDarkMode ? '#000' : '#FFF',
              borderRadius: '6px',
              position: 'relative',
              overflow: 'hidden',
              '.MuiTabs-indicator': {
                display: 'none',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                height: '100%',
                width: '100%',
                borderRadius: '6px',
                transition: 'transform 0.3s ease-in-out',
                zIndex: 1,
                transform: `translateX(${(manualIndex ?? activeIndex) * 100}%)`,
              },
            }}
            variant="scrollable"
            scrollButtons="auto"
            value={manualIndex ?? activeIndex}
            onChange={actions?.editLayout?.display ? null : handleTabChange}
          >
            {tabs &&
              tabs
                ?.filter((tab) => tab?.label || tab?.name)
                ?.map((tab, index) => {
                  return (
                    <Tab
                      label={tab?.label || tab?.name}
                      key={'tab' + tab?.label || tab?.name}
                      style={{
                        fontWeight:
                          (manualIndex ?? activeIndex) === index
                            ? '600'
                            : '500',
                        textTransform: 'capitalize',
                        padding: '10px 20px 10px 20px',
                        fontSize: '13px',
                      }}
                      sx={{
                        position: 'relative',
                        zIndex: 2,
                        minHeight: '42px',
                        maxHeight: '42px',
                        height: '42px',
                        color: isDarkMode
                          ? '#FFFFFF !important'
                          : (manualIndex ?? activeIndex) !== index
                            ? '#000000 !important'
                            : '#FFFFFF !important',
                        backgroundColor:
                          (manualIndex ?? activeIndex) === index
                            ? businessPreference?.mainColor
                            : 'transparent',
                        minWidth: isMobile ? '30px' : '',
                        borderRadius: '6px',
                        transition: 'color 0.3s ease-in-out',
                        '&:hover': {
                          color: '#FFFFFF',
                        },
                      }}
                    />
                  );
                })}
          </Tabs>

        </Box>

      </Toolbar>
    </div>
  );
};

export default TopNavBar;
